import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MapPin, Activity, Heart, AlertTriangle, Clock, Users, Star, Repeat } from 'lucide-react';
import PathActivityVideo from './PathActivityVideo';

interface OverviewMetrics {
  location: string;
  timeOnFeet: number;
  lastCheckIn: number;
  dailySteps: number;
  heartRate: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface HomeOverviewProps {
  onNavigate?: (view: string) => void;
}

const HomeOverview: React.FC<HomeOverviewProps> = ({ onNavigate }) => {
  // Mock data - in real app this would come from floor sensors and other devices
  const currentMetrics: OverviewMetrics = {
    location: 'Living Room',
    timeOnFeet: 45,
    lastCheckIn: 5,
    dailySteps: 2500,
    heartRate: 72,
    riskLevel: 'LOW'
  };

  const activityTrends = [
    { week: 'Week 1', activity: 85, prediction: 88 },
    { week: 'Week 2', activity: 82, prediction: 86 },
    { week: 'Week 3', activity: 78, prediction: 85 },
    { week: 'Week 4', activity: 75, prediction: 82 },
    { week: 'Week 5', activity: null, prediction: 80 },
    { week: 'Week 6', activity: null, prediction: 78 }
  ];

  const recentAlerts = [
    {
      type: 'BEHAVIORAL',
      priority: 'MEDIUM',
      message: 'Changes in sleep pattern detected',
      timestamp: new Date(Date.now() - 3600000)
    },
    {
      type: 'FALL_RISK',
      priority: 'HIGH',
      message: 'Increased unsteady gait detected',
      timestamp: new Date(Date.now() - 7200000)
    },
    {
      type: 'ROUTINE',
      priority: 'LOW',
      message: 'Medication schedule adjustment recommended',
      timestamp: new Date(Date.now() - 10800000)
    }
  ];

  const featuredSections = [
    { name: 'Fall Detection', view: 'falls', description: 'Monitor and prevent potential falls', icon: <AlertTriangle />, color: 'bg-red-100 text-red-800', value: '0', unit: 'in last 7 days' },
    { name: 'Recovery Monitoring', view: 'recovery', description: 'Track rehabilitation progress after injury', icon: <Repeat />, color: 'bg-blue-100 text-blue-800', value: '85%', unit: 'of phase complete' },
    { name: 'Caregiver Portal', view: 'caregiver', description: 'Connect with care team and family', icon: <Users />, color: 'bg-purple-100 text-purple-800', value: '2', unit: 'new updates' },
    { name: 'Morning Routine', view: 'morning-routine', description: 'Smart assistance for morning activities', icon: <Clock />, color: 'bg-amber-100 text-amber-800', value: '6:30', unit: 'next scheduled' }
  ];

  const getStatusColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'LOW':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Handle navigation when a section is clicked
  const handleNavigation = (view: string) => {
    if (onNavigate) {
      onNavigate(view);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Emma Martinez</h2>
          <p className="text-gray-600">Age: 78 | Room: 204</p>
        </div>
        <div className={`px-4 py-2 rounded-full ${getStatusColor(currentMetrics.riskLevel)} bg-opacity-10`}>
          Status: {currentMetrics.riskLevel}
        </div>
      </div>
      
      {/* Featured Sections */}
      <div className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredSections.map((section, index) => (
            <div 
              key={index} 
              className="bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-300 cursor-pointer"
              onClick={() => handleNavigation(section.view)}
            >
              <div className="p-5">
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg ${section.color}`}>
                    {section.icon}
                  </div>
                  <h3 className="ml-3 text-lg font-medium text-gray-900">{section.name}</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">{section.description}</p>
                <div className="flex justify-between items-end">
                  <div>
                    <span className="text-2xl font-bold">{section.value}</span>
                    <span className="text-sm text-gray-500 ml-1">{section.unit}</span>
                  </div>
                  <div className="text-sm text-blue-600">View Details →</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <PathActivityVideo 
            videoUrl="/videos/emma_martinez_activity.mp4"
            timestamp={new Date().toLocaleTimeString()}
            activityType="Walking to Kitchen"
          />
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <MapPin className="text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold">Current Location</h3>
            </div>
            <p className="text-3xl font-bold mb-2">{currentMetrics.location}</p>
            <p className="text-sm text-gray-500">
              Last check-in: {formatMinutes(currentMetrics.lastCheckIn)} ago
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <Activity className="text-green-500 mr-2" />
              <h3 className="text-lg font-semibold">Activity Level</h3>
            </div>
            <p className="text-3xl font-bold mb-2">{currentMetrics.dailySteps}</p>
            <p className="text-sm text-gray-500">
              Time on feet today: {formatMinutes(currentMetrics.timeOnFeet)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <Heart className="text-red-500 mr-2" />
              <h3 className="text-lg font-semibold">Vital Signs</h3>
            </div>
            <p className="text-3xl font-bold mb-2">{currentMetrics.heartRate} BPM</p>
            <p className="text-sm text-gray-500">Heart rate - Normal range</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Activity Trends & Predictions</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activityTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="activity" 
                stroke="#8884d8" 
                name="Actual Activity"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="prediction" 
                stroke="#82ca9d" 
                name="Predicted Activity"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              Activity level shows a gradual decline over the past 4 weeks.
              Early intervention recommended to maintain independence.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Recent Alerts</h3>
          <div className="space-y-4">
            {recentAlerts.map((alert, index) => (
              <div key={index} className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <AlertTriangle className={`
                    ${alert.priority === 'HIGH' ? 'text-red-500' : 
                      alert.priority === 'MEDIUM' ? 'text-yellow-500' : 
                      'text-blue-500'}`} 
                  />
                </div>
                <div>
                  <p className="font-medium">{alert.message}</p>
                  <p className="text-sm text-gray-500">
                    {alert.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-right">
            <button 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              onClick={() => handleNavigation('emergency')}
            >
              View All Alerts →
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Daily Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <Clock className="text-blue-500 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Routine Adherence</h4>
                <p className="text-gray-600">Morning routine completed on schedule. Medication taken at 8:00 AM.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <Users className="text-green-500 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Social Engagement</h4>
                <p className="text-gray-600">Video call with family scheduled for 2:00 PM today.</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <Activity className="text-purple-500 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Physical Activity</h4>
                <p className="text-gray-600">Morning exercise completed. Recommended: afternoon walk.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <Star className="text-yellow-500 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Wellness Score</h4>
                <p className="text-gray-600">Current score: 85/100 - Maintaining good overall health.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeOverview; 